/**
 * Super Admin route definitions (/admin/system/*)
 * Extracted from Router.tsx to keep each file under 500 lines
 */

import React, { lazy, Suspense } from 'react'

import { Route, Navigate } from 'react-router-dom'

import { ProtectedRoute } from '@/platform/auth/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'

import { LoadingSpinner } from './LoadingSpinner'
import { RedirectKeepingLocation } from './RedirectKeepingLocation'
import {
  SuperAdminLayout,
  SystemDashboardPage,
  TenantsPage,
  TenantDetailPage,
  SystemUsersPage,
  UserDetailPage,
  AuditActivityPage,
  TicketsPage,
  TicketDetailPage,
  ImportMappingsPage,
  AdminCustomImporterPage,
  AdaptersPage,
  UnknownContractsPage,
  MarketDataInventoryTab,
  MarketDataDownloadTab,
  MarketDataJobsTab,
  AssetsPage,
  AssetDetailPage,
  AssetFormPage,
  AssetImportPage,
  EmailTemplatesPage,
  EmailLogsPage,
  EmailSettingsPage,
  JobsPage,
  RevenueDashboardPage,
  MaintenancePage,
  PricingPlansPage,
  RateLimitingPage,
  WaitlistPage,
  TaxRulesPage,
  ReadinessPage,
  ExchangesPage,
  ExchangeTemplateEditorPage,
  TransactionAttributesPage,
  ApiImportMappingsListPage,
  ApiImportMappingsDetailPage,
  ApiImportMappingsCreatePage,
  PermissionsMatrixPage,
  AuthAuditPage,
  AuditReportsPage,
  GdprManagementPage,
  BroadcastsPage,
  SSOManagementPage,
  BulkOperationsPage,
  BulkOperationDetailPage,
} from './routes'

// «Ajustes» (#2828): Languages, Asset types and Platform types as tabs of one page.
// Used only here, so it is lazy-loaded locally instead of through `routes.tsx`
// (which is close to the project ≤500 LOC ceiling).
const SettingsPage = lazy(() =>
  import('@/modules/admin/features/super-admin/settings').then((m) => ({
    default: m.SettingsPage,
  })),
)

// «Configuración del Sistema»: configuration keys + feature flags, used only here as well.
const PlatformConfigPage = lazy(() =>
  import('@/modules/admin/features/super-admin/platform-config').then((m) => ({
    default: m.PlatformConfigPage,
  })),
)

// The «Correo» layout (header + tab bar) is used only here, so it is lazy-loaded
// locally instead of through `routes.tsx`, which is close to its size limit.
const EmailLayout = lazy(() =>
  import('@/modules/admin/features/super-admin/email/pages/EmailLayout').then((m) => ({
    default: m.EmailLayout,
  })),
)

// «Estado del sistema» (#2893): layout and summary page are used only here, so they are lazy-loaded locally too.
const SystemStatusLayout = lazy(() =>
  import('@/modules/admin/features/super-admin/system-health/pages/SystemStatusLayout').then(
    (m) => ({ default: m.SystemStatusLayout }),
  ),
)
const SystemStatusSummaryPage = lazy(() =>
  import('@/modules/admin/features/super-admin/system-health/pages/SystemStatusSummaryPage').then(
    (m) => ({ default: m.SystemStatusSummaryPage }),
  ),
)

// The «Auditoría» layout (header + tab bar) is used only here, so it is lazy-loaded locally like «Correo».
const AuditLayout = lazy(() =>
  import('@/modules/admin/features/super-admin/audit/AuditLayout').then((m) => ({
    default: m.AuditLayout,
  })),
)

/**
 * Super Admin routes - accessible only by super_admin role
 * Renders as a nested route under /admin/system
 */
export const SuperAdminRoutes = () => (
  <Route
    path="/admin/system"
    element={
      <ProtectedRoute fallback="/login" requiredRoles={['super_admin']}>
        <Suspense fallback={<LoadingSpinner />}>
          <SuperAdminLayout />
        </Suspense>
      </ProtectedRoute>
    }
  >
    <Route index element={<Navigate to="readiness" replace />} />
    {/* «Platform readiness»: can the platform serve each country, source and catalogue today (the landing). */}
    <Route
      path="readiness"
      element={
        <RouteErrorBoundary routeName="system-readiness">
          <Suspense fallback={<LoadingSpinner />}>
            <ReadinessPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="dashboard"
      element={
        <RouteErrorBoundary routeName="system-dashboard">
          <Suspense fallback={<LoadingSpinner />}>
            <SystemDashboardPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="tenants"
      element={
        <RouteErrorBoundary routeName="system-tenants">
          <Suspense fallback={<LoadingSpinner />}>
            <TenantsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="tenants/:id"
      element={
        <RouteErrorBoundary routeName="system-tenant-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <TenantDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="users"
      element={
        <RouteErrorBoundary routeName="system-users">
          <Suspense fallback={<LoadingSpinner />}>
            <SystemUsersPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="users/:id"
      element={
        <RouteErrorBoundary routeName="system-user-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <UserDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* «Auditoría» is one screen with three tabs that are routes (#3896). The layout route has NO path on
        purpose, like «Correo»: the children keep their full `audit/…` paths because the route guards read this
        file with a regex and a relative `activity` would resolve to /admin/system/activity. The four old
        addresses (`audit`, `audit-log`, `auth-audit`, `audit-reports`) redirect keeping query and hash. */}
    <Route
      path="audit"
      element={<RedirectKeepingLocation pathname="/admin/system/audit/activity" />}
    />
    <Route
      element={
        <RouteErrorBoundary routeName="system-audit">
          <Suspense fallback={<LoadingSpinner />}>
            <AuditLayout />
          </Suspense>
        </RouteErrorBoundary>
      }
    >
      <Route
        path="audit/activity"
        element={
          <RouteErrorBoundary routeName="system-audit-activity">
            <Suspense fallback={<LoadingSpinner />}>
              <AuditActivityPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path="audit/access"
        element={
          <RouteErrorBoundary routeName="system-audit-access">
            <Suspense fallback={<LoadingSpinner />}>
              <AuthAuditPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path="audit/reports"
        element={
          <RouteErrorBoundary routeName="system-audit-reports">
            <Suspense fallback={<LoadingSpinner />}>
              <AuditReportsPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
    </Route>
    <Route
      path="audit/*"
      element={<RedirectKeepingLocation pathname="/admin/system/audit/activity" />}
    />
    <Route
      path="audit-log"
      element={<RedirectKeepingLocation pathname="/admin/system/audit/activity" />}
    />
    {/* «Configuración del Sistema» (/config): the platform's business configuration (every declared
        key, validated by its schema on the server, with overrides, history and diff) and the feature
        flags (?tab=flags). Not to be confused with /settings, the catalogues page («Ajustes»). */}
    <Route
      path="config"
      element={
        <RouteErrorBoundary routeName="system-config">
          <Suspense fallback={<LoadingSpinner />}>
            <PlatformConfigPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="settings"
      element={
        <RouteErrorBoundary routeName="system-settings">
          <Suspense fallback={<LoadingSpinner />}>
            <SettingsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="support"
      element={
        <RouteErrorBoundary routeName="system-support">
          <Suspense fallback={<LoadingSpinner />}>
            <TicketsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="support/:ticketId"
      element={
        <RouteErrorBoundary routeName="system-ticket-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <TicketDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="import-mappings"
      element={
        <RouteErrorBoundary routeName="system-import-mappings">
          <Suspense fallback={<LoadingSpinner />}>
            <ImportMappingsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* The full-page create editor was retired (issue 2840): it created mappings with no exchange. The old
        address opens the create dialog of the list. */}
    <Route
      path="import-mappings/new"
      element={<Navigate to="/admin/system/import-mappings?modal=create" replace />}
    />
    <Route
      path="import-mappings/:id/edit"
      element={
        <RouteErrorBoundary routeName="system-import-mappings-edit">
          <Suspense fallback={<LoadingSpinner />}>
            <AdminCustomImporterPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* A source document maps its vocabulary onto ONE catalogue attribute (docs/IMPORTS.md §4): the attributes
        page shows which adapters speak each one. The old per-exchange «attribute mappings» land there. */}
    <Route
      path="attribute-mappings"
      element={<RedirectKeepingLocation pathname="/admin/system/attributes" />}
    />
    {/* «Import adapters»: every exchange API and chain explorer under one lifecycle (draft, cases, impact,
        activation, history), plus the unknown-contracts queue. The blockchain templates / networks /
        classification screens were three views of the same documents: their addresses land here. */}
    <Route
      path="adapters"
      element={
        <RouteErrorBoundary routeName="system-adapters">
          <Suspense fallback={<LoadingSpinner />}>
            <AdaptersPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="adapters/unknown-contracts"
      element={
        <RouteErrorBoundary routeName="system-adapters-unknown-contracts">
          <Suspense fallback={<LoadingSpinner />}>
            <UnknownContractsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="adapters/exchange/:id"
      element={
        <RouteErrorBoundary routeName="system-adapter-exchange">
          <Suspense fallback={<LoadingSpinner />}>
            <AdaptersPage family="exchange" />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="adapters/chain/:id"
      element={
        <RouteErrorBoundary routeName="system-adapter-chain">
          <Suspense fallback={<LoadingSpinner />}>
            <AdaptersPage family="chain" />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="blockchain-classification"
      element={<Navigate to="/admin/system/adapters/unknown-contracts" replace />}
    />
    <Route path="blockchain-templates" element={<Navigate to="/admin/system/adapters" replace />} />
    <Route path="blockchain-templates/:id" element={<RedirectKeepingLocation pathname="/admin/system/adapters/chain/:id" />} />
    <Route path="blockchain-networks" element={<Navigate to="/admin/system/adapters" replace />} />
    {/* report templates live in each country's tax-config workbench */}
    <Route path="pdf-templates" element={<Navigate to="/admin/system/tax-configs" replace />} />
    <Route path="pdf-templates/:id" element={<Navigate to="/admin/system/tax-configs" replace />} />
    {/* Market Data Management */}
    <Route path="market-data" element={<Navigate to="inventory" replace />} />
    <Route
      path="market-data/inventory"
      element={
        <RouteErrorBoundary routeName="system-market-data-inventory">
          <Suspense fallback={<LoadingSpinner />}>
            <MarketDataInventoryTab />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="market-data/download"
      element={
        <RouteErrorBoundary routeName="system-market-data-download">
          <Suspense fallback={<LoadingSpinner />}>
            <MarketDataDownloadTab />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="market-data/jobs"
      element={
        <RouteErrorBoundary routeName="system-market-data-jobs">
          <Suspense fallback={<LoadingSpinner />}>
            <MarketDataJobsTab />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Assets Management */}
    <Route
      path="assets"
      element={
        <RouteErrorBoundary routeName="system-assets">
          <Suspense fallback={<LoadingSpinner />}>
            <AssetsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="assets/new"
      element={
        <RouteErrorBoundary routeName="system-assets-new">
          <Suspense fallback={<LoadingSpinner />}>
            <AssetFormPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="assets/import"
      element={
        <RouteErrorBoundary routeName="system-assets-import">
          <Suspense fallback={<LoadingSpinner />}>
            <AssetImportPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="assets/:id"
      element={
        <RouteErrorBoundary routeName="system-asset-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <AssetDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="assets/:id/edit"
      element={
        <RouteErrorBoundary routeName="system-asset-edit">
          <Suspense fallback={<LoadingSpinner />}>
            <AssetFormPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Email System Management: one screen, three tabs that are routes (#2898).
        The layout route has NO path on purpose: the children keep their `email/…` paths,
        because the route guards read this file with a regex and a relative `logs` would
        resolve to /admin/system/logs, which does not exist. */}
    <Route path="email" element={<RedirectKeepingLocation pathname="logs" />} />
    <Route
      element={
        <RouteErrorBoundary routeName="system-email">
          <Suspense fallback={<LoadingSpinner />}>
            <EmailLayout />
          </Suspense>
        </RouteErrorBoundary>
      }
    >
      <Route
        path="email/logs"
        element={
          <RouteErrorBoundary routeName="system-email-logs">
            <Suspense fallback={<LoadingSpinner />}>
              <EmailLogsPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path="email/templates"
        element={
          <RouteErrorBoundary routeName="system-email-templates">
            <Suspense fallback={<LoadingSpinner />}>
              <EmailTemplatesPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route
        path="email/settings"
        element={
          <RouteErrorBoundary routeName="system-email-settings">
            <Suspense fallback={<LoadingSpinner />}>
              <EmailSettingsPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
    </Route>
    <Route
      path="email/*"
      element={<RedirectKeepingLocation pathname="/admin/system/email/logs" />}
    />
    {/* Trabajos: el monitor único de todos los trabajos en segundo plano (#2891) */}
    <Route
      path="jobs"
      element={
        <RouteErrorBoundary routeName="system-jobs">
          <Suspense fallback={<LoadingSpinner />}>
            <JobsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* «Monitorización de Sincronización» (#2892) was retired: «Trabajos» (jobs_family) replaces it.
        Old bookmarks land on the import family. */}
    <Route path="sync" element={<Navigate to="/admin/system/jobs?jobs_family=import" replace />} />
    {/* The platform takes no backups (PITR is infrastructure): «Recuperación» shows the recovery posture. */}
    <Route path="backups" element={<Navigate to="/admin/system/health?tab=recovery" replace />} />
    {/* «Recuperación» and «Rendimiento» are tabs of «Estado del sistema». */}
    <Route path="recovery" element={<Navigate to="/admin/system/health?tab=recovery" replace />} />
    <Route
      path="performance"
      element={<Navigate to="/admin/system/health?tab=performance" replace />}
    />
    {/* Estado del sistema: one screen whose tabs are routes (#2893). Like «Correo», the layout route has
        NO path: the children keep their full health/… paths because the route guards read this file with a
        regex. */}
    <Route path="health" element={<RedirectKeepingLocation pathname="summary" />} />
    <Route
      element={
        <RouteErrorBoundary routeName="system-status">
          <Suspense fallback={<LoadingSpinner />}>
            <SystemStatusLayout />
          </Suspense>
        </RouteErrorBoundary>
      }
    >
      <Route
        path="health/summary"
        element={
          <RouteErrorBoundary routeName="system-status-summary">
            <Suspense fallback={<LoadingSpinner />}>
              <SystemStatusSummaryPage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
    </Route>
    <Route
      path="health/*"
      element={<RedirectKeepingLocation pathname="/admin/system/health/summary" />}
    />
    {/* Revenue Analytics */}
    <Route
      path="revenue"
      element={
        <RouteErrorBoundary routeName="system-revenue">
          <Suspense fallback={<LoadingSpinner />}>
            <RevenueDashboardPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Maintenance Mode */}
    <Route
      path="maintenance"
      element={
        <RouteErrorBoundary routeName="system-maintenance">
          <Suspense fallback={<LoadingSpinner />}>
            <MaintenancePage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Pricing Plans */}
    <Route
      path="pricing-plans"
      element={
        <RouteErrorBoundary routeName="system-pricing-plans">
          <Suspense fallback={<LoadingSpinner />}>
            <PricingPlansPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* «Feature Flags» is the flags tab of «Configuración del Sistema». */}
    <Route path="feature-flags" element={<Navigate to="/admin/system/config?tab=flags" replace />} />
    {/* The people waiting for sign-ups to open */}
    <Route
      path="waitlist"
      element={
        <RouteErrorBoundary routeName="system-waitlist">
          <Suspense fallback={<LoadingSpinner />}>
            <WaitlistPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Rate Limiting */}
    <Route
      path="rate-limiting"
      element={
        <RouteErrorBoundary routeName="system-rate-limiting">
          <Suspense fallback={<LoadingSpinner />}>
            <RateLimitingPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Tax Configurations */}
    <Route
      path="tax-configs"
      element={
        <RouteErrorBoundary routeName="system-tax-configs">
          <Suspense fallback={<LoadingSpinner />}>
            <TaxRulesPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Exchanges Management */}
    <Route
      path="exchanges"
      element={
        <RouteErrorBoundary routeName="system-exchanges">
          <Suspense fallback={<LoadingSpinner />}>
            <ExchangesPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Old URL of the API template editor (issue 2839): bookmarks land on the new one with their id,
        their query and their anchor. */}
    <Route
      path="exchanges/templates/:id/edit"
      element={
        <RedirectKeepingLocation pathname="/admin/system/api-import-mappings/templates/:id/edit" />
      }
    />
    {/* Transaction Attributes Catalog (issue #279) */}
    <Route
      path="attributes"
      element={
        <RouteErrorBoundary routeName="system-attributes">
          <Suspense fallback={<LoadingSpinner />}>
            <TransactionAttributesPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Languages Catalog (issue #330) and the asset-type / platform-type catalogs
        (BUG-80 / PR2) are, since #2828, tabs of «Ajustes». The old URLs (and the
        bookmarks to them) land on their tab. `exchange-types` is the tab
        `platform-types`: the screen calls them platforms. */}
    <Route
      path="languages"
      element={
        <RedirectKeepingLocation
          pathname="/admin/system/settings"
          setQuery={{ tab: 'languages' }}
        />
      }
    />
    <Route
      path="catalog/asset-types"
      element={
        <RedirectKeepingLocation
          pathname="/admin/system/settings"
          setQuery={{ tab: 'asset-types' }}
        />
      }
    />
    <Route
      path="catalog/exchange-types"
      element={
        <RedirectKeepingLocation
          pathname="/admin/system/settings"
          setQuery={{ tab: 'platform-types' }}
        />
      }
    />
    {/* The company-sizes catalog was retired: its URL lands on «Ajustes». */}
    <Route path="catalog/company-sizes" element={<Navigate to="/admin/system/settings" replace />} />
    {/* API Import Mappings — list + create + detail (Config/Endpoints/Templates per exchange).
        Order matters: `/new` must precede `/:slug` so it isn't captured as a slug. */}
    <Route
      path="api-import-mappings"
      element={
        <RouteErrorBoundary routeName="system-api-import-mappings">
          <Suspense fallback={<LoadingSpinner />}>
            <ApiImportMappingsListPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="api-import-mappings/new"
      element={
        <RouteErrorBoundary routeName="system-api-import-mappings-create">
          <Suspense fallback={<LoadingSpinner />}>
            <ApiImportMappingsCreatePage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Visual editor of the API import templates. It lives under the section it is reached from. */}
    <Route
      path="api-import-mappings/templates/:id/edit"
      element={
        <RouteErrorBoundary routeName="system-api-import-mapping-template-editor">
          <Suspense fallback={<LoadingSpinner />}>
            <ExchangeTemplateEditorPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="api-import-mappings/:slug"
      element={
        <RouteErrorBoundary routeName="system-api-import-mappings-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <ApiImportMappingsDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* Permissions Matrix */}
    <Route
      path="permissions-matrix"
      element={
        <RouteErrorBoundary routeName="system-permissions-matrix">
          <Suspense fallback={<LoadingSpinner />}>
            <PermissionsMatrixPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* La recuperación de cuenta se retiró (#2879): su URL lleva a «Usuarios». */}
    <Route path="account-recovery" element={<Navigate to="/admin/system/users" replace />} />
    <Route
      path="auth-audit"
      element={<RedirectKeepingLocation pathname="/admin/system/audit/access" />}
    />
    {/* Tenant Impersonation — the standalone page was removed as a duplicate:
        impersonating is a row action on Tenants, and the session history now
        lives on the Tenants page. */}
    <Route path="tenant-impersonation" element={<Navigate to="/admin/system/tenants" replace />} />
    {/* Analytics — the hidden «Analíticas» screen was retired (#4117): it repeated the figures of «Inicio» and «Ingresos»
        and read a table nobody wrote. The old URL lands on «Inicio». */}
    <Route path="analytics" element={<Navigate to="/admin/system/dashboard" replace />} />
    <Route
      path="audit-reports"
      element={<RedirectKeepingLocation pathname="/admin/system/audit/reports" />}
    />
    {/* GDPR Management */}
    <Route
      path="gdpr"
      element={
        <RouteErrorBoundary routeName="system-gdpr">
          <Suspense fallback={<LoadingSpinner />}>
            <GdprManagementPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* The Help Center was removed: every old `help-center/…` URL lands on «Inicio». */}
    <Route path="help-center" element={<Navigate to="/admin/system/dashboard" replace />} />
    <Route path="help-center/*" element={<Navigate to="/admin/system/dashboard" replace />} />
    {/* «Avisos» (#2899): las difusiones. Las tres URL de «Gestión de Notificaciones» llevan aquí. */}
    <Route
      path="broadcasts"
      element={
        <RouteErrorBoundary routeName="system-broadcasts">
          <Suspense fallback={<LoadingSpinner />}>
            <BroadcastsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route path="notifications" element={<Navigate to="/admin/system/broadcasts" replace />} />
    <Route
      path="notifications/management"
      element={<Navigate to="/admin/system/broadcasts" replace />}
    />
    <Route
      path="notifications/preferences"
      element={<Navigate to="/admin/system/broadcasts" replace />}
    />
    {/* «Claves API de plataforma» was retired (#2886): a copy of the organisation screen over the same endpoint. Old links land on the dashboard. */}
    <Route path="platform-api-keys" element={<Navigate to="/admin/system/dashboard" replace />} />
    {/* SSO Management */}
    <Route
      path="sso-management"
      element={
        <RouteErrorBoundary routeName="system-sso-management">
          <Suspense fallback={<LoadingSpinner />}>
            <SSOManagementPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    {/* «Configuración SSO» se retiró (#2884): su URL lleva a «SSO». */}
    <Route path="sso-settings" element={<Navigate to="/admin/system/sso-management" replace />} />
    {/* The generic «Plantillas» were retired (#2838): no module read what they stored. Every old URL lands on the e-mail templates, where a one-time notice says where each kind of template lives now. */}
    <Route
      path="templates/*"
      element={<Navigate to="/admin/system/email/templates?retirada=plantillas" replace />}
    />
    {/* Tenant onboarding was retired: «Organizaciones › Crear» creates the tenant with plan and invited admin. */}
    <Route path="tenant-onboarding" element={<Navigate to="/admin/system/tenants" replace />} />
    {/* Bulk Operations */}
    <Route
      path="bulk-operations"
      element={
        <RouteErrorBoundary routeName="system-bulk-operations">
          <Suspense fallback={<LoadingSpinner />}>
            <BulkOperationsPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
    <Route
      path="bulk-operations/:operationId"
      element={
        <RouteErrorBoundary routeName="system-bulk-operation-detail">
          <Suspense fallback={<LoadingSpinner />}>
            <BulkOperationDetailPage />
          </Suspense>
        </RouteErrorBoundary>
      }
    />
  </Route>
)
